import { useEffect, useRef, useState } from 'react';

import { NotebookSheet } from '@/shared/ui';

import { getIllustrationLayers } from '../data/illustration';

import styles from './IllustrationFrame.module.css';

interface IllustrationFrameProps {
  /** 현재까지 등록된 코드 수 (0..3). */
  revealedCount: number;
  /** 외부 sizing 조정용 추가 클래스. */
  className?: string;
  /** 폴라로이드 사진 아래, 같은 카드 안에 함께 렌더할 부가 콘텐츠 (예: ProgressDots). */
  trailing?: React.ReactNode;
}

/**
 * 합동 일러스트 조립 프레임 — "노트 페이지에 끼워둔 폴라로이드" 컨셉.
 *
 * 외곽: `NotebookSheet`(스프링 바인딩 + 뜯긴 가장자리 + 종이 그레인)로 감싼다.
 * 내부:
 *  - 폴라로이드 형태의 사진 (흰 보더 + 살짝 회전 + 잉크 그림자)
 *  - 진행도(0~3)에 따라 단계별 레이어가 페이드인된다.
 *  - 3/3 도달 시: 1회 한정 카타르시스 + 영구 도장
 *
 * `revealedCount` 가 클수록 색감/완성도가 회복된다 (0/3 ≈ 흑백, 3/3 = 풀컬러).
 * `prefers-reduced-motion: reduce` 환경은 globals.css 가 모션을 페이드 수준으로 축소.
 */
export function IllustrationFrame({
  revealedCount,
  className,
  trailing,
}: IllustrationFrameProps): React.JSX.Element {
  const total = Math.max(0, Math.min(3, revealedCount));
  const isComplete = total >= 3;

  const status = ((): string => {
    if (isComplete) {
      return '🎉 다락 유치원의 하루를 함께해주셔서 감사합니다.';
    }
    if (total === 0) {
      return '2026 다락 유치원의 하루';
    }
    return `조금씩 보이기 시작했어요!`;
  })();

  /**
   * 진행도에 따른 일러스트 레이어 구성.
   *
   * - 0/3: /1.png 단독.
   * - 1/3, 2/3: /1.png 위에 /2.png 또는 /3.png 패치를 페이드인 오버레이.
   * - 3/3: /reward.png 단독 (오버레이 없음).
   */
  const layers = getIllustrationLayers(total);

  /** 1회 한정 완성 카타르시스. */
  const [showCelebration, setShowCelebration] = useState(false);
  const celebratedRef = useRef(false);
  useEffect(() => {
    if (!isComplete || celebratedRef.current) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    const FLAG = 'darak-kidday/celebrated-v3';
    if (window.sessionStorage.getItem(FLAG) === '1') {
      return;
    }
    celebratedRef.current = true;
    setShowCelebration(true);
    window.sessionStorage.setItem(FLAG, '1');
    const timer = window.setTimeout(() => {
      setShowCelebration(false);
    }, 2600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isComplete]);

  return (
    <NotebookSheet
      flat
      className={[styles.sheet, className].filter(Boolean).join(' ')}
      aria-label="합동 일러스트 조립 영역"
    >
      <div className={styles.heading}>
        <span className={styles.headingTitle}>오늘의 유치원 사진</span>
        <span className={styles.headingDate}>2026.05.05</span>
      </div>

      <figure className={styles.polaroid}>
        <div className={styles.canvas}>
          <img
            src={layers.base}
            alt={
              isComplete
                ? '다락캐스트 어린이날 합동 일러스트 — 풀컬러로 완성된 상태'
                : `다락캐스트 어린이날 합동 일러스트 — ${String(total)}/3 진행 중`
            }
            className={styles.image}
            draggable={false}
          />

          {layers.overlay !== null ? (
            <img
              key={layers.overlay.key}
              src={layers.overlay.src}
              alt=""
              className={styles.overlayPatch}
              draggable={false}
              aria-hidden
            />
          ) : null}

          {showCelebration ? <CelebrationLayer /> : null}
          {/* {isComplete ? <CompletionStamp /> : null} */}
        </div>

        <figcaption className={styles.caption} aria-live="polite">
          {status}
        </figcaption>
      </figure>

      {trailing !== undefined && trailing !== null ? (
        <div className={styles.trailing}>{trailing}</div>
      ) : null}
    </NotebookSheet>
  );
}

/**
 * 3/3 완성 시 1회 재생되는 카타르시스 레이어.
 * 가장자리에서 안쪽으로 별·하트 파티클이 솟아오르고, 중앙에 "어린이날 축하해요!" 핸드라이팅.
 */
function CelebrationLayer(): React.JSX.Element {
  return (
    <div className={styles.celebration} aria-hidden>
      <svg className={styles.celebrationSvg} viewBox="0 0 1280 720" preserveAspectRatio="xMidYMid slice">
        <Particle x={140} y={160} delay={0} color="var(--color-crayon-yellow)" shape="star" />
        <Particle x={1120} y={200} delay={120} color="var(--color-crayon-pink)" shape="heart" />
        <Particle x={220} y={580} delay={240} color="var(--color-crayon-mint)" shape="star" />
        <Particle x={1080} y={620} delay={360} color="var(--color-crayon-orange)" shape="heart" />
        <Particle x={640} y={120} delay={180} color="var(--color-crayon-red)" shape="star" />
        <Particle x={400} y={640} delay={300} color="var(--color-crayon-blue)" shape="star" />
        <Particle x={900} y={580} delay={420} color="var(--color-crayon-yellow)" shape="heart" />

        <text
          x={640}
          y={380}
          textAnchor="middle"
          fontFamily="Gaegu, cursive"
          fontSize={104}
          fontWeight={700}
          fill="var(--color-ink)"
          className={styles.celebrationText}
        >
          어린이날 축하해요!
        </text>
      </svg>
    </div>
  );
}

interface ParticleProps {
  x: number;
  y: number;
  delay: number;
  color: string;
  shape: 'star' | 'heart';
}

function Particle({ x, y, delay, color, shape }: ParticleProps): React.JSX.Element {
  const path =
    shape === 'star'
      ? 'M 0 -32 L 9 -9 L 32 -9 L 14 5 L 21 28 L 0 14 L -21 28 L -14 5 L -32 -9 L -9 -9 Z'
      : 'M 0 26 L -26 -2 Q -26 -18 -12 -18 Q 0 -18 0 -7 Q 0 -18 12 -18 Q 26 -18 26 -2 Z';
  return (
    <g
      className={styles.particle}
      style={{ animationDelay: `${String(delay)}ms` }}
      transform={`translate(${String(x)}, ${String(y)})`}
    >
      <path d={path} fill={color} stroke="var(--color-ink)" strokeWidth={2.5} strokeLinejoin="round" />
    </g>
  );
}
